import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  DollarSign,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Banknote,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import CategorySelect from '@/components/ui/CategorySelect';

export default function CompanyDebts() {
  const { token } = useAuth();
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [debtCategories, setDebtCategories] = useState([]);
  
  // Debt dialog
  const [debtDialog, setDebtDialog] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [debtForm, setDebtForm] = useState({
    creditor_name: '',
    description: '',
    original_amount: '',
    due_date: '',
    category: '',
    notes: '',
    is_installment: false,
    installment_count: '',
    installment_amount: '',
    first_due_date: ''
  });

  // Installments tab
  const [activeTab, setActiveTab] = useState('regular'); // 'regular' | 'installments'
  const [installmentPayDialog, setInstallmentPayDialog] = useState(false);
  const [payingInstallmentsDebt, setPayingInstallmentsDebt] = useState(null);
  const [selectedInstallments, setSelectedInstallments] = useState(new Set());
  const [installmentPayForm, setInstallmentPayForm] = useState({ payment_method: 'card', notes: '' });
  
  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [payingDebt, setPayingDebt] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    payment_method: 'card'
  });
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingDebt, setDeletingDebt] = useState(null);
  
  // Expanded debts (to show payment history)
  const [expandedDebts, setExpandedDebts] = useState(new Set());

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchDebts();
    fetchSummary();
  }, [statusFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebtCategories(response.data.debt_categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to defaults
      setDebtCategories(['Loan', 'Supplier', 'Vehicle', 'Equipment', 'Service', 'Rent', 'Tax', 'Insurance', 'Other']);
    }
  };

  const fetchDebts = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/debts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebts(response.data);
    } catch (error) {
      console.error('Error fetching debts:', error);
      toast.error('Failed to load debts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/debts/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const openPayInstallments = (debt) => {
    setPayingInstallmentsDebt(debt);
    setSelectedInstallments(new Set());
    setInstallmentPayForm({ payment_method: 'card', notes: '' });
    setInstallmentPayDialog(true);
  };

  const submitPayInstallments = async () => {
    if (!payingInstallmentsDebt || selectedInstallments.size === 0) {
      toast.error('Select at least one installment');
      return;
    }
    try {
      const numbers = Array.from(selectedInstallments).sort((a, b) => a - b);
      const body = { numbers, payment_method: installmentPayForm.payment_method, notes: installmentPayForm.notes || null };
      await axios.post(`${API}/debts/${payingInstallmentsDebt.id}/pay-installments`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${numbers.length} installment(s) marked as paid`);
      setInstallmentPayDialog(false);
      setPayingInstallmentsDebt(null);
      setSelectedInstallments(new Set());
      fetchDebts();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pay installments');
    }
  };

  const handleCreateDebt = () => {
    setEditingDebt(null);
    setDebtForm({
      creditor_name: '',
      description: '',
      original_amount: '',
      due_date: '',
      category: '',
      notes: '',
      is_installment: activeTab === 'installments',
      installment_count: '',
      installment_amount: '',
      first_due_date: ''
    });
    setDebtDialog(true);
  };

  const handleEditDebt = (debt) => {
    setEditingDebt(debt);
    setDebtForm({
      creditor_name: debt.creditor_name || '',
      description: debt.description || '',
      original_amount: debt.original_amount?.toString() || '',
      due_date: (() => {
        if (!debt.due_date) return '';
        try { return new Date(debt.due_date).toISOString().split('T')[0]; }
        catch { return debt.due_date; }
      })(),
      category: debt.category || '',
      notes: debt.notes || '',
      is_installment: !!debt.is_installment,
      installment_count: '',
      installment_amount: '',
      first_due_date: ''
    });
    setDebtDialog(true);
  };

  const handleSaveDebt = async () => {
    if (!debtForm.creditor_name || !debtForm.original_amount) {
      toast.error('Please fill in creditor name and amount');
      return;
    }

    try {
      const data = {
        creditor_name: debtForm.creditor_name,
        description: debtForm.description || null,
        original_amount: parseFloat(debtForm.original_amount),
        due_date: debtForm.due_date || null,
        category: debtForm.category || null,
        notes: debtForm.notes || null
      };
      if (debtForm.is_installment && !editingDebt) {
        data.is_installment = true;
        if (debtForm.installment_count) data.installment_count = parseInt(debtForm.installment_count, 10);
        if (debtForm.installment_amount) data.installment_amount = parseFloat(debtForm.installment_amount);
        if (debtForm.first_due_date) data.first_due_date = debtForm.first_due_date;
      }

      if (editingDebt) {
        await axios.put(`${API}/debts/${editingDebt.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Debt updated successfully');
      } else {
        await axios.post(`${API}/debts`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Debt created successfully');
      }

      setDebtDialog(false);
      fetchDebts();
      fetchSummary();
    } catch (error) {
      console.error('Error saving debt:', error);
      toast.error(error.response?.data?.detail || 'Failed to save debt');
    }
  };

  const handleDeleteDebt = async () => {
    if (!deletingDebt) return;

    try {
      await axios.delete(`${API}/debts/${deletingDebt.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Debt deleted successfully');
      setDeleteDialog(false);
      setDeletingDebt(null);
      fetchDebts();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting debt:', error);
      toast.error('Failed to delete debt');
    }
  };

  const handleOpenPayment = (debt) => {
    setPayingDebt(debt);
    setPaymentForm({
      amount: debt.remaining_amount?.toString() || '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      payment_method: 'card'
    });
    setPaymentDialog(true);
  };

  const handleMakePayment = async () => {
    if (!payingDebt || !paymentForm.amount) {
      toast.error('Please enter payment amount');
      return;
    }

    const amount = parseFloat(paymentForm.amount);
    if (amount <= 0) {
      toast.error('Payment amount must be positive');
      return;
    }

    if (amount > payingDebt.remaining_amount) {
      toast.error(`Payment cannot exceed remaining debt (€${(payingDebt.remaining_amount || 0).toFixed(2)})`);
      return;
    }

    try {
      const response = await axios.post(`${API}/debts/${payingDebt.id}/payments`, {
        amount: amount,
        date: paymentForm.date || null,
        notes: paymentForm.notes || null,
        payment_method: paymentForm.payment_method || 'card'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const methodLabel = paymentForm.payment_method === 'cash' ? 'Cash Outcome' : 'Card Outcome';
      toast.success(
        response.data.status === 'paid' 
          ? `Debt fully paid! Payment recorded in ${methodLabel}.` 
          : `Payment recorded in ${methodLabel}.`
      );
      
      setPaymentDialog(false);
      setPayingDebt(null);
      fetchDebts();
      fetchSummary();
    } catch (error) {
      console.error('Error making payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const toggleExpanded = (debtId) => {
    setExpandedDebts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(debtId)) {
        newSet.delete(debtId);
      } else {
        newSet.add(debtId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Active', className: 'bg-blue-100 text-blue-800', icon: Clock },
      overdue: { label: 'Overdue', className: 'bg-red-100 text-red-800', icon: AlertTriangle },
      paid: { label: 'Paid', className: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    const { label, className, icon: Icon } = config[status] || config.active;
    return (
      <Badge className={`${className} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="h-8 w-8 text-slate-700" />
            <div>
              <h1 className="text-2xl font-bold text-black">Company Debts</h1>
              <p className="text-slate-600">Track and manage company debts and payments</p>
            </div>
          </div>
          <Button onClick={handleCreateDebt} data-testid="add-debt-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Debt
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-700">Total Remaining</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-800">€{summary.total_remaining_amount?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-red-600 mt-1">{summary.active_count + summary.overdue_count} active debts</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700">Total Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-800">€{summary.total_paid_amount?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-green-600 mt-1">{summary.paid_count} debts cleared</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-amber-700">Overdue</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-800">{summary.overdue_count || 0}</p>
              <p className="text-xs text-amber-600 mt-1">debts past due date</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">Original Total</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-slate-800">€{summary.total_original_amount?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-slate-600 mt-1">{summary.total_count} total debts</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <Label className="text-sm font-medium">Filter by Status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Debts</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('regular')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'regular' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-regular-debts"
        >
          Regular Debts
        </button>
        <button
          onClick={() => setActiveTab('installments')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'installments' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-installments"
        >
          Installments ({debts.filter(d => d.is_installment).length})
        </button>
      </div>

      {/* Debts List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-4"></div>
            <p className="text-slate-500">Loading debts...</p>
          </div>
        ) : debts.filter(d => activeTab === 'installments' ? d.is_installment : !d.is_installment).length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No {activeTab === 'installments' ? 'installment debts' : 'debts'} found</p>
            <p className="text-sm text-slate-400 mt-1">Click "Add Debt" to create one</p>
          </div>
        ) : (
          debts.filter(d => activeTab === 'installments' ? d.is_installment : !d.is_installment).map(debt => {
            const origAmount = debt.original_amount || 0;
            const remAmount = debt.remaining_amount || 0;
            const paidPercent = origAmount > 0 ? ((1 - remAmount / origAmount) * 100) : 0;
            return (
            <div key={debt.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              {/* Debt Header */}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{debt.creditor_name || 'Unknown'}</h3>
                      {getStatusBadge(debt.status)}
                      {debt.category && (
                        <Badge variant="outline" className="text-xs">{debt.category}</Badge>
                      )}
                    </div>
                    {debt.description && (
                      <p className="text-sm text-slate-600 mb-2">{debt.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        Original: €{debt.original_amount?.toFixed(2)}
                      </span>
                      <span className={`flex items-center gap-1 font-semibold ${debt.remaining_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        <Banknote className="h-4 w-4" />
                        Remaining: €{debt.remaining_amount?.toFixed(2)}
                      </span>
                      {debt.due_date && (
                        <span className={`flex items-center gap-1 ${debt.status === 'overdue' ? 'text-red-600' : ''}`}>
                          <Calendar className="h-4 w-4" />
                          Due: {formatDate(debt.due_date)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {debt.status !== 'paid' && (
                      <Button 
                        size="sm" 
                        onClick={() => handleOpenPayment(debt)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`pay-debt-${debt.id}`}
                      >
                        <Banknote className="h-4 w-4 mr-1" />
                        Pay
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEditDebt(debt)}
                      data-testid={`edit-debt-${debt.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setDeletingDebt(debt); setDeleteDialog(true); }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-debt-${debt.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Payment Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Payment Progress</span>
                    <span>{paidPercent.toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${paidPercent}%` }}
                    ></div>
                  </div>
                </div>

                {/* Installments schedule */}
                {debt.is_installment && Array.isArray(debt.installments) && debt.installments.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-3" data-testid={`installments-${debt.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-slate-700">
                        Installments · Paid {debt.installments.filter(i => i.paid).length}/{debt.installments.length}
                      </div>
                      {debt.installments.some(i => !i.paid) && (
                        <Button
                          size="sm"
                          onClick={() => openPayInstallments(debt)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                          data-testid={`pay-installments-btn-${debt.id}`}
                        >
                          Pay Installments
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                      {debt.installments.map((ins) => {
                        const isLate = !ins.paid && ins.due_date && new Date(ins.due_date) < new Date();
                        return (
                          <div
                            key={ins.number}
                            className={`text-[10px] px-1.5 py-1 rounded border text-center ${
                              ins.paid ? 'bg-emerald-50 border-emerald-300 text-emerald-800' :
                              isLate ? 'bg-rose-50 border-rose-300 text-rose-800' :
                              'bg-slate-50 border-slate-200 text-slate-700'
                            }`}
                            title={ins.due_date ? `Due ${ins.due_date}` : 'No due date'}
                            data-testid={`installment-${debt.id}-${ins.number}`}
                          >
                            <div className="font-bold">#{ins.number}</div>
                            <div>€{(ins.amount || 0).toFixed(0)}</div>
                            {ins.due_date && <div className="opacity-70">{ins.due_date.slice(5)}</div>}
                            {ins.paid && <div className="opacity-70">✓</div>}
                            {!ins.paid && isLate && <div className="opacity-70">Late</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Expand/Collapse Payments */}
                {debt.payments && debt.payments.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpanded(debt.id)}
                    className="mt-3 text-slate-600"
                  >
                    {expandedDebts.has(debt.id) ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Hide Payment History ({debt.payments.length})
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        Show Payment History ({debt.payments.length})
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Payment History */}
              {expandedDebts.has(debt.id) && debt.payments && debt.payments.length > 0 && (
                <div className="border-t border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Payment History</h4>
                  <div className="space-y-2">
                    {debt.payments.map((payment, idx) => (
                      <div key={payment.id || idx} className="flex items-center justify-between bg-white p-3 rounded border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">€{payment.amount?.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">{formatDate(payment.date)}</p>
                          </div>
                        </div>
                        {payment.notes && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {payment.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );})
        )}
      </div>

      {/* Add/Edit Debt Dialog */}
      <Dialog open={debtDialog} onOpenChange={setDebtDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDebt ? 'Edit Debt' : 'Add New Debt'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Creditor Name *</Label>
              <Input
                value={debtForm.creditor_name}
                onChange={(e) => setDebtForm({...debtForm, creditor_name: e.target.value})}
                placeholder="Who do you owe?"
                data-testid="debt-creditor-input"
              />
            </div>
            
            <div>
              <Label>Amount (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={debtForm.original_amount}
                onChange={(e) => setDebtForm({...debtForm, original_amount: e.target.value})}
                placeholder="0.00"
                data-testid="debt-amount-input"
              />
            </div>
            
            <div>
              <Label>Category</Label>
              <CategorySelect
                value={debtForm.category}
                onValueChange={(v) => setDebtForm({...debtForm, category: v})}
                categories={debtCategories}
                categoryType="debt"
                placeholder="Select category"
                token={token}
                onCategoryAdded={fetchCategories}
                testId="debt-category-select"
              />
            </div>
            
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={debtForm.due_date}
                onChange={(e) => setDebtForm({...debtForm, due_date: e.target.value})}
                data-testid="debt-duedate-input"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={debtForm.description}
                onChange={(e) => setDebtForm({...debtForm, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={debtForm.notes}
                onChange={(e) => setDebtForm({...debtForm, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Installment fields (only when creating a new installment debt) */}
            {!editingDebt && (
              <div className="border-t border-slate-200 pt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={debtForm.is_installment}
                    onChange={(e) => setDebtForm({...debtForm, is_installment: e.target.checked})}
                    data-testid="is-installment-checkbox"
                  />
                  This debt is paid in installments
                </label>
                {debtForm.is_installment && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-6">
                    <div>
                      <Label className="text-xs">Number of installments</Label>
                      <Input
                        type="number"
                        min="1"
                        value={debtForm.installment_count}
                        onChange={(e) => setDebtForm({...debtForm, installment_count: e.target.value})}
                        placeholder="e.g. 12"
                        data-testid="installment-count"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Amount per installment (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={debtForm.installment_amount}
                        onChange={(e) => setDebtForm({...debtForm, installment_amount: e.target.value})}
                        placeholder="Auto if empty"
                        data-testid="installment-amount"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">First due date (optional)</Label>
                      <Input
                        type="date"
                        value={debtForm.first_due_date}
                        onChange={(e) => setDebtForm({...debtForm, first_due_date: e.target.value})}
                        data-testid="first-due-date"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDebtDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveDebt} data-testid="save-debt-btn">
              {editingDebt ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {payingDebt && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Paying debt to:</p>
                <p className="font-semibold text-lg">{payingDebt.creditor_name}</p>
                <p className="text-sm text-red-600">Remaining: €{payingDebt.remaining_amount?.toFixed(2)}</p>
              </div>
              
              <div>
                <Label>Payment Amount (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={payingDebt.remaining_amount}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder="0.00"
                  data-testid="payment-amount-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter full amount to pay off completely, or partial amount
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Payment Method *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="payment-method-card"
                    onClick={() => setPaymentForm({...paymentForm, payment_method: 'card'})}
                    className={`flex items-center justify-center gap-2 p-3 rounded-md border-2 transition-all ${
                      paymentForm.payment_method === 'card'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span className="font-medium text-sm">Card</span>
                  </button>
                  <button
                    type="button"
                    data-testid="payment-method-cash"
                    onClick={() => setPaymentForm({...paymentForm, payment_method: 'cash'})}
                    className={`flex items-center justify-center gap-2 p-3 rounded-md border-2 transition-all ${
                      paymentForm.payment_method === 'cash'
                        ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Banknote className="h-5 w-5" />
                    <span className="font-medium text-sm">Cash</span>
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                  {paymentForm.payment_method === 'card' 
                    ? 'Will be recorded in Card Outcome' 
                    : 'Will be recorded in Cash Outcome'}
                </p>
              </div>
              
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm({...paymentForm, date: e.target.value})}
                  data-testid="payment-date-input"
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Input
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  placeholder="Payment reference, bank transfer, etc."
                />
              </div>
              
              <div className={`p-3 rounded-lg text-sm ${paymentForm.payment_method === 'cash' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                <p className="font-medium">Note:</p>
                <p>This payment will be recorded in <strong>{paymentForm.payment_method === 'cash' ? 'Cash Outcome' : 'Card Outcome'}</strong> in Company Financials.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleMakePayment} className="bg-green-600 hover:bg-green-700" data-testid="confirm-payment-btn">
              <Banknote className="h-4 w-4 mr-2" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Installments Dialog */}
      <Dialog open={installmentPayDialog} onOpenChange={setInstallmentPayDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Pay Installments</DialogTitle>
          </DialogHeader>
          {payingInstallmentsDebt && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Creditor:</p>
                <p className="font-semibold text-lg">{payingInstallmentsDebt.creditor_name}</p>
                <p className="text-sm text-red-600">Remaining: €{payingInstallmentsDebt.remaining_amount?.toFixed(2)}</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Select installments to pay</Label>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      onClick={() => {
                        const unpaid = (payingInstallmentsDebt.installments || [])
                          .filter(i => !i.paid)
                          .map(i => i.number);
                        setSelectedInstallments(new Set(unpaid));
                      }}
                      data-testid="select-all-unpaid-installments"
                    >
                      Select all unpaid
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      type="button"
                      className="text-slate-600 hover:underline"
                      onClick={() => setSelectedInstallments(new Set())}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto divide-y divide-slate-100">
                  {(payingInstallmentsDebt.installments || []).map((ins) => {
                    const isLate = !ins.paid && ins.due_date && new Date(ins.due_date) < new Date();
                    const disabled = !!ins.paid;
                    const checked = selectedInstallments.has(ins.number);
                    return (
                      <label
                        key={ins.number}
                        className={`flex items-center gap-3 px-3 py-2 text-sm ${disabled ? 'bg-emerald-50 opacity-70' : 'cursor-pointer hover:bg-slate-50'}`}
                        data-testid={`installment-row-${payingInstallmentsDebt.id}-${ins.number}`}
                      >
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(selectedInstallments);
                            if (e.target.checked) next.add(ins.number);
                            else next.delete(ins.number);
                            setSelectedInstallments(next);
                          }}
                          data-testid={`installment-check-${ins.number}`}
                        />
                        <div className="flex-1 flex items-center gap-2">
                          <span className="font-medium">#{ins.number}</span>
                          <span>€{(ins.amount || 0).toFixed(2)}</span>
                          {ins.due_date && (
                            <span className={`text-xs ${isLate ? 'text-rose-600' : 'text-slate-500'}`}>
                              · due {ins.due_date}
                              {isLate && ' (late)'}
                            </span>
                          )}
                        </div>
                        {ins.paid && (
                          <span className="text-xs text-emerald-700 font-medium">Paid</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-center justify-between">
                <span className="text-sm text-slate-700">
                  {selectedInstallments.size} installment{selectedInstallments.size !== 1 ? 's' : ''} selected
                </span>
                <span className="text-lg font-bold text-blue-700" data-testid="installments-total-selected">
                  €{
                    (payingInstallmentsDebt.installments || [])
                      .filter(i => selectedInstallments.has(i.number))
                      .reduce((s, i) => s + (i.amount || 0), 0)
                      .toFixed(2)
                  }
                </span>
              </div>

              <div>
                <Label className="mb-2 block">Payment Method *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="installment-method-card"
                    onClick={() => setInstallmentPayForm({...installmentPayForm, payment_method: 'card'})}
                    className={`flex items-center justify-center gap-2 p-3 rounded-md border-2 transition-all ${
                      installmentPayForm.payment_method === 'card'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span className="font-medium text-sm">Card</span>
                  </button>
                  <button
                    type="button"
                    data-testid="installment-method-cash"
                    onClick={() => setInstallmentPayForm({...installmentPayForm, payment_method: 'cash'})}
                    className={`flex items-center justify-center gap-2 p-3 rounded-md border-2 transition-all ${
                      installmentPayForm.payment_method === 'cash'
                        ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Banknote className="h-5 w-5" />
                    <span className="font-medium text-sm">Cash</span>
                  </button>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={installmentPayForm.notes}
                  onChange={(e) => setInstallmentPayForm({...installmentPayForm, notes: e.target.value})}
                  placeholder="Optional notes"
                  data-testid="installment-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstallmentPayDialog(false)}>Cancel</Button>
            <Button
              onClick={submitPayInstallments}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={selectedInstallments.size === 0}
              data-testid="confirm-pay-installments-btn"
            >
              <Banknote className="h-4 w-4 mr-2" />
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Debt</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this debt to "{deletingDebt?.creditor_name}"? 
              This will also remove all payment records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDebt} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
